import { Box, Stack } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { PierResponseDto, VesselVisitDto } from '@planning/app/api'
import { InitializationWrapper } from '@planning/components'
import { useVesselVisitBreadcrumbs } from '@planning/hooks/useVesselVisitBreadcrumbs'
import { IBerthItem } from '@planning/rt-stores/berth/BerthItem'
import { IEntityStore } from '@planning/rt-stores/types'
import { vesselService, vesselVisitService } from '@planning/services'
import berthService from '@planning/services/berthService'
import companyService from '@planning/services/companyService'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, ConfirmationDialog } from '@tom-ui/ui'
import { PATHS, withAuthenticationRequired } from '@tom-ui/utils'
import _ from 'lodash'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { BerthPlanning } from '../BerthPlanning/BerthPlanning'
import { BerthPlanningViewStore } from '../BerthPlanning/Stores/BerthPlanningViewStore'
import { VesselVisitPlanningPageHeader } from './Components/molecules/VesselVisitPlanningPageHeader'
import { VesselVisitPlanningForm2 } from './Components/templates/VesselVisitForm'
import {
  VesselVisitFormStore,
  VesselVisitFormStoreInitialData,
} from './Stores/VesselVisitFormStore'
import { VoyageDto } from './Stores/VesselVisitGeneralInformationFormStore'

export const VesselVisitPlanningPage: FC = withAuthenticationRequired(
  observer(() => {
    const { id } = useParams()
    const { vesselVisitPlanningStore, vesselVisitItemStore, berthItemStore, pierItemStore } =
      usePlanningStore()

    const [loading, setLoading] = useState(true)

    const berthPlanningViewStore = useMemo(
      () => new BerthPlanningViewStore(berthItemStore, vesselVisitItemStore, pierItemStore),
      [berthItemStore, vesselVisitItemStore, pierItemStore],
    )

    useEffect(() => {
      const requests = [berthPlanningViewStore.fetchPiers(), berthPlanningViewStore.fetchVisits()]

      if (id) {
        vesselVisitPlanningStore.setIsEditMode(true)
        const vesselVisitId = Number(id)

        Promise.all(requests).then(async () => {
          const vesselVisit = await vesselVisitService.getById(vesselVisitId)
          vesselVisitItemStore.upsertBulk([vesselVisit])
          const vesselVisitItem = vesselVisitItemStore.getById(vesselVisitId)
          vesselVisitPlanningStore.setVesselVisit(vesselVisitItem)

          if (vesselVisit) {
            const berths = await berthService.get()
            berthItemStore.upsertBulk(berths)

            berthPlanningViewStore.setVesselVisitSelectedId(vesselVisitId)
            berthPlanningViewStore.setSelectedVessels(
              vesselVisitPlanningStore.vesselVisit?.vessels.map(v => v.data) ?? [],
            )
            berthPlanningViewStore.setStartDateIfNewDateHasADifferentStart(
              vesselVisit.etb ?? vesselVisit.eta,
            )

            berthPlanningViewStore.setSelectPierId(
              berths.find(b => vesselVisit?.berthIds?.includes(b.id))?.pierId ?? '',
            )

            berthPlanningViewStore.setSelectedQmmFrom(vesselVisit?.qmmFrom ?? 0)
            berthPlanningViewStore.setSelectedQmmTo(vesselVisit?.qmmTo ?? 0)
            berthPlanningViewStore.setSelectedBerths(berthPlanningViewStore.berthsByQmmRange)

            const vessels = await vesselService.getByIds(vesselVisit.carrierIds)
            vesselVisitPlanningStore.setVessels(vessels)

            const companyIds = [
              vesselVisit.shippingAgentId,
              vesselVisit.vesselOperatorId,
              ...(vesselVisit.voyageNumbers?.map(v => v.shippingLineId) ?? []),
            ].filter((id): id is number => !!id)
            const companies = await Promise.all(companyIds.map(id => companyService.getById(id)))

            vesselVisitPlanningStore.setCompanies(companies)
          }
          setLoading(false)
        })
      } else {
        vesselVisitPlanningStore.setIsEditMode(false)
        vesselVisitPlanningStore.reset()
        setLoading(false)
        Promise.all(requests)
      }
    }, [berthItemStore, berthPlanningViewStore, id, vesselVisitItemStore, vesselVisitPlanningStore])

    useEffect(() => {
      const fetchBerth = async () => {
        const berths = await berthService.get()
        berthItemStore.upsertBulk(berths)
      }

      if (!id) {
        setLoading(true)
        fetchBerth()
          .then(() => {
            berthPlanningViewStore.setSelectPierId(berthPlanningViewStore.piers[0]?.id ?? '')
          })
          .finally(() => {
            setLoading(false)
          })
      }
    }, [id, berthItemStore, berthPlanningViewStore])

    return (
      <InitializationWrapper isInitializing={loading}>
        <VesselVisitPlanning
          berthPlanningViewStore={berthPlanningViewStore}
          berthItemStore={berthItemStore}
        />
      </InitializationWrapper>
    )
  }),
)

type Props = {
  berthPlanningViewStore: BerthPlanningViewStore
  berthItemStore: IEntityStore<IBerthItem>
}

const VesselVisitPlanning: FC<Props> = observer(
  ({ berthPlanningViewStore, berthItemStore }: Props) => {
    const { t } = useTranslate()
    const navigate = useNavigate()
    const { appViewStore, vesselVisitPlanningStore } = usePlanningStore()

    const { breadcrumbs } = useVesselVisitBreadcrumbs(vesselVisitPlanningStore.vesselVisit)

    const location = useLocation()

    const formStore = useMemo(() => {
      const vv = vesselVisitPlanningStore.vesselVisit?.data
      const companyById = vesselVisitPlanningStore.companyById

      const pier = getInitialPier(vv, berthItemStore, berthPlanningViewStore)

      const initialData: VesselVisitFormStoreInitialData = {
        vesselVisit: vv,
        vessels: vesselVisitPlanningStore.vessels,
        vesselOperator: vv?.vesselOperatorId ? companyById[vv.vesselOperatorId] : undefined,
        shippingAgent: vv?.shippingAgentId ? companyById[vv.shippingAgentId] : undefined,
        voyages: vv?.voyageNumbers?.map<VoyageDto>(v => ({
          id: v.id,
          inboundNumber: v.inboundVoyageNumber,
          outboundNumber: v.outboundVoyageNumber,
          shippingLine: v.shippingLineId ? companyById[v.shippingLineId] : undefined,
        })),
        pier: pier ?? null,
        berths: berthPlanningViewStore.berthsByQmmRange,
      }

      return new VesselVisitFormStore(initialData)
    }, [
      vesselVisitPlanningStore.vesselVisit?.data,
      vesselVisitPlanningStore.companyById,
      vesselVisitPlanningStore.vessels,
      berthPlanningViewStore,
      berthItemStore,
    ])

    const goBackToOrigin = (cameFrom: string, id?: number) => {
      const basePath = PATHS.visits.vesselVisits.url

      if (cameFrom === 'dashboard') {
        navigate(`${basePath}/${id}/dashboard`)
      } else if (cameFrom === 'vessel-visits') {
        navigate(PATHS.visits.vesselVisits.url)
      } else {
        navigate(basePath)
      }
    }

    useEffect(() => {
      const disposer = reaction(
        () => formStore.data,
        formValues => {
          const selectedVessel = formStore.data.generalInformation?.vessel
          berthPlanningViewStore.applyFormStoreDataToPlanning(formValues, selectedVessel)
        },
      )

      return () => {
        disposer()
      }
    }, [berthPlanningViewStore, formStore.data])

    const title = vesselVisitPlanningStore.isEditMode
      ? t('Edit vessel visit')
      : t('Create vessel visit')

    const onFormSubmit = async () => {
      if (formStore.validate()) {
        if (vesselVisitPlanningStore.vesselVisit?.id) {
          if (formStore.isDirty) {
            await vesselVisitPlanningStore.saveVesselVisitFromFormStore(
              vesselVisitPlanningStore.vesselVisit.id,
              formStore.data,
              berthPlanningViewStore.selectedPierId,
            )
          }
        } else {
          await vesselVisitPlanningStore.createVesselVisitFromFormStore(
            formStore.data,
            berthPlanningViewStore.selectedPierId,
          )

          vesselVisitPlanningStore.reset()
        }

        goBackToOrigin(location.state?.from, vesselVisitPlanningStore.vesselVisit?.id)

        appViewStore.setShowAlert(
          'success',
          t('vesselVisitSubmitSuccessfully', 'Vessel visit submit successfully'),
        )
      }
    }

    return (
      <Box sx={{ height: '100%' }}>
        <Breadcrumbs items={breadcrumbs} />
        <VesselVisitPlanningPageHeader
          pageTitle={title}
          submitButtonLabel={
            vesselVisitPlanningStore.isEditMode ? t('saveAll', 'Save all') : t('submit', 'Submit')
          }
          onBack={() => {
            if (formStore.isDirty) {
              berthPlanningViewStore.toggleConfirmationDialog(true)
            } else {
              goBackToOrigin(location.state?.from, vesselVisitPlanningStore.vesselVisit?.id)
              vesselVisitPlanningStore.reset()
            }
          }}
          onSubmit={onFormSubmit}
        />

        <Stack
          direction='row'
          spacing={1}
          sx={{
            height: 'calc(100% - 162px)',
          }}
        >
          <Stack
            sx={{
              padding: '0 1rem 1rem 1rem',
              height: '100%',
              overflowY: 'auto',
              flex: 2,
            }}
          >
            <VesselVisitPlanningForm2
              formStore={formStore}
              berthPlanningViewStore={berthPlanningViewStore}
              vesselVisitPlanningStore={vesselVisitPlanningStore}
            />
          </Stack>
          <Stack
            sx={{
              flex: 5,
              height: '100%',
              overflowY: 'auto',
              padding: 1,
            }}
          >
            <BerthPlanning store={berthPlanningViewStore} />
          </Stack>
        </Stack>

        <ConfirmationDialog
          open={berthPlanningViewStore.isDialogOpen}
          title={t('discardChanges', 'Discard changes?')}
          message={
            vesselVisitPlanningStore.isEditMode
              ? t('unsavedChangesWarning', 'You will lose unsaved changes.')
              : t('allDataWillBeLost', 'All entered data will be lost.')
          }
          primaryActionText={t('discard', 'Discard')}
          closeLabel={t('keepEditing', 'Keep editing')}
          maxWidth='xs'
          blockClickOutside
          onConfirm={() => {
            goBackToOrigin(location.state?.from, vesselVisitPlanningStore.vesselVisit?.id)

            vesselVisitPlanningStore.reset()
          }}
          onClose={() => {
            berthPlanningViewStore.toggleConfirmationDialog(false)
          }}
        />
      </Box>
    )
  },
)

function getInitialPier(
  vesselVisit: VesselVisitDto | undefined,
  berthItemStore: IEntityStore<IBerthItem>,
  berthPlanningViewStore: BerthPlanningViewStore,
) {
  let pier: PierResponseDto | undefined = undefined
  if (vesselVisit?.berthIds?.length) {
    const berthId = vesselVisit?.berthIds?.[0] ?? ''
    const berth = _(berthItemStore.elements).get(berthId)

    pier = berth?.pier?.data
  } else {
    pier = berthPlanningViewStore.piers[0]
  }
  return pier
}
